import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button, Card, Col, DatePicker, Empty, Grid, Modal, Pagination, Row, Select, Space, Spin, Statistic, Table, Tabs, Typography, message
} from 'antd';
import {
  BarChartOutlined, DownloadOutlined, FileExcelOutlined, FilePdfOutlined,
  PieChartOutlined, ReloadOutlined, SettingOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../../utils/api';
import FinancialSummary from '../../components/FinancialSummary';
import useFinancialRefresh from '../../hooks/useFinancialRefresh';
import { generateIncomeStatementPdf } from '../../services/incomeStatementPdfService';
import { generateFinancialPositionPdf } from '../../services/financialPositionPdfService';
import { generateTrialBalancePdf } from '../../services/trialBalancePdfService';
import './AccountingReportsPage.css';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

const REPORTS = [
  { key: 'trial-balance', label: 'งบทดลอง' },
  { key: 'income-statement', label: 'งบกำไรขาดทุน' },
  { key: 'financial-position', label: 'งบแสดงฐานะการเงิน' },
  { key: 'general-ledger', label: 'บัญชีแยกประเภท' }
];

const TYPE_LABELS = {
  asset: 'สินทรัพย์', liability: 'หนี้สิน', equity: 'ส่วนของเจ้าของ', income: 'รายได้', expense: 'ค่าใช้จ่าย'
};

const QUICK_FILTERS = [
  ['today', 'วันนี้'], ['yesterday', 'เมื่อวาน'], ['this-month', 'เดือนนี้'],
  ['last-month', 'เดือนที่แล้ว'], ['this-year', 'ปีนี้'], ['last-year', 'ปีที่แล้ว']
];

const money = value => Number(value || 0).toLocaleString('th-TH', {
  minimumFractionDigits: 2, maximumFractionDigits: 2
});

const quickPeriod = key => {
  const now = dayjs();
  if (key === 'today') return [now.startOf('day'), now.endOf('day')];
  if (key === 'yesterday') return [now.subtract(1, 'day').startOf('day'), now.subtract(1, 'day').endOf('day')];
  if (key === 'this-month') return [now.startOf('month'), now.endOf('month')];
  if (key === 'last-month') return [now.subtract(1, 'month').startOf('month'), now.subtract(1, 'month').endOf('month')];
  if (key === 'last-year') return [now.subtract(1, 'year').startOf('year'), now.subtract(1, 'year').endOf('year')];
  return [now.startOf('year'), now.endOf('year')];
};

const sum = (rows, field) => rows.reduce((total, row) => total + Number(row[field] || 0), 0);
const roundMoney = value => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

const AccountingReportsPage = () => {
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = screens.sm === false;
  const [report, setReport] = useState('trial-balance');
  const [period, setPeriod] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);
  const [asOfDate, setAsOfDate] = useState(dayjs());
  const [activeQuick, setActiveQuick] = useState('this-month');
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState();
  const [rows, setRows] = useState([]);
  const [reportData, setReportData] = useState({});
  const [loading, setLoading] = useState(false);
  const reportRequest = useRef(0);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  const [pdfDataUrl, setPdfDataUrl] = useState('');
  const [mobilePage, setMobilePage] = useState(1);
  const mobilePageSize = 10;

  useEffect(() => {
    api.get('/api/accounting/accounts').then(response => {
      const list = Array.isArray(response.data.data) ? response.data.data : [];
      const normalized = list.map(account => ({
        ...account,
        id: account.id ?? account.ID ?? account.account_id,
        code: account.code ?? account.CODE ?? account.account_code ?? '',
        name: account.name ?? account.NAME ?? account.account_name ?? ''
      })).filter(account => account.id && (account.code || account.name));
      setAccounts(normalized);
      if (normalized.length) setAccountId(current => current || normalized[0].id);
    }).catch(() => message.error('ไม่สามารถโหลดรายชื่อผังบัญชีได้'));
  }, []);

  const loadReport = useCallback(async (override = {}) => {
    const selectedReport = override.report || report;
    const selectedPeriod = override.period || period;
    const selectedAsOfDate = override.asOfDate || asOfDate;
    const selectedAccountId = override.accountId ?? accountId;
    if (selectedReport === 'general-ledger' && !selectedAccountId) return;
    const request = ++reportRequest.current;
    setLoading(true);
    setRows([]);
    setReportData({});
    setPdfDataUrl('');
    try {
      const params = selectedReport === 'financial-position'
        ? { end_date: selectedAsOfDate.format('YYYY-MM-DD') }
        : {
          start_date: selectedPeriod[0].format('YYYY-MM-DD'),
          end_date: selectedPeriod[1].format('YYYY-MM-DD'),
          ...(selectedReport === 'general-ledger' ? { account_id: selectedAccountId } : {})
        };
      const response = await api.get(`/api/accounting/reports/${selectedReport}`, { params });
      const data = response.data.data || {};
      if (request !== reportRequest.current) return;
      setReportData(data);
      setRows(Array.isArray(data) ? data : (data.rows || []));
      setMobilePage(1);
    } catch (error) {
      if (request !== reportRequest.current) return;
      setRows([]);
      setReportData({});
      message.error(error.response?.data?.message || 'โหลดรายงานไม่สำเร็จ');
    } finally {
      if (request === reportRequest.current) setLoading(false);
    }
  }, [accountId, asOfDate, period, report]);

  useEffect(() => {
    if (report !== 'general-ledger' || accountId) loadReport();
  }, [report, accountId]); // eslint-disable-line react-hooks/exhaustive-deps

  useFinancialRefresh(loadReport);

  const applyQuickFilter = key => {
    const next = quickPeriod(key);
    setPeriod(next);
    setAsOfDate(next[1]);
    setActiveQuick(key);
    loadReport({ period: next, asOfDate: next[1] });
  };

  const changeReport = key => {
    setReport(key);
    setRows([]);
    setReportData({});
    setMobilePage(1);
  };

  const trialTotals = useMemo(() => ({
    openingDebit: sum(rows, 'opening_debit'), openingCredit: sum(rows, 'opening_credit'),
    debit: sum(rows, 'total_debit'), credit: sum(rows, 'total_credit'),
    closingDebit: sum(rows, 'closing_debit'), closingCredit: sum(rows, 'closing_credit')
  }), [rows]);

  const trialDifference = useMemo(() => roundMoney(
    Math.abs(roundMoney(trialTotals.closingDebit) - roundMoney(trialTotals.closingCredit))
  ), [trialTotals.closingCredit, trialTotals.closingDebit]);

  const positionTotals = useMemo(() => ({
    asset: rows.filter(row => row.type === 'asset').reduce((total, row) => total + Number(row.amount || 0), 0),
    liability: rows.filter(row => row.type === 'liability').reduce((total, row) => total + Number(row.amount || 0), 0),
    equity: rows.filter(row => row.type === 'equity').reduce((total, row) => total + Number(row.amount || 0), 0)
  }), [rows]);

  const columns = useMemo(() => {
    if (report === 'trial-balance') return [
      { title: 'ชื่อบัญชี', dataIndex: 'name', fixed: 'left', width: 230 },
      { title: 'รหัสบัญชี', dataIndex: 'code', width: 130 },
      { title: 'ยอดยกมา', children: [
        { title: 'เดบิต', dataIndex: 'opening_debit', align: 'right', width: 130, render: money },
        { title: 'เครดิต', dataIndex: 'opening_credit', align: 'right', width: 130, render: money }
      ] },
      { title: 'งวด', children: [
        { title: 'เดบิต', dataIndex: 'total_debit', align: 'right', width: 130, render: money },
        { title: 'เครดิต', dataIndex: 'total_credit', align: 'right', width: 130, render: money }
      ] },
      { title: 'คงเหลือ', children: [
        { title: 'เดบิต', dataIndex: 'closing_debit', align: 'right', width: 130, render: money },
        { title: 'เครดิต', dataIndex: 'closing_credit', align: 'right', width: 130, render: money }
      ] }
    ];
    if (report === 'general-ledger') return [
      { title: 'วันที่', dataIndex: 'entry_date', width: 120, render: value => dayjs(value).format('DD/MM/YYYY') },
      { title: 'เลขที่เอกสาร', dataIndex: 'jv_number', width: 150 },
      { title: 'คำอธิบาย', key: 'description', width: 300, render: (_, row) => row.description || row.entry_description || '-' },
      { title: 'เดบิต', dataIndex: 'debit', align: 'right', width: 130, render: money },
      { title: 'เครดิต', dataIndex: 'credit', align: 'right', width: 130, render: money },
      { title: 'ยอดคงเหลือ', dataIndex: 'running_balance', align: 'right', width: 140, render: money }
    ];
    if (report === 'income-statement') return [
      { title: 'ชื่อบัญชี', dataIndex: 'name' },
      { title: 'รหัสบัญชี', dataIndex: 'code', width: 180 },
      { title: 'ประเภทบัญชี', dataIndex: 'type', width: 180, render: type => <span className={`account-tag ${type}`}>{TYPE_LABELS[type] || type}</span> },
      { title: 'ประจำงวด', key: 'amount', align: 'right', width: 180, render: (_, row) => money(row.type === 'expense' ? -Number(row.amount) : row.amount) },
      { title: 'จนถึงงวดนี้', key: 'closing', align: 'right', width: 180, render: (_, row) => money(row.type === 'expense' ? -Number(row.cumulative_amount) : row.cumulative_amount) }
    ];
    return [
      { title: 'รหัสบัญชี', dataIndex: 'code', width: 180 },
      { title: 'ชื่อบัญชี', dataIndex: 'name' },
      { title: 'ประเภทบัญชี', dataIndex: 'type', width: 180, render: type => <span className={`account-tag ${type}`}>{TYPE_LABELS[type] || type}</span> },
      { title: 'ยอดคงเหลือ', dataIndex: 'amount', align: 'right', width: 200, render: money }
    ];
  }, [report]);

  const summaryCards = () => {
    if (report === 'income-statement') return [
      ['รายได้รวม', reportData.revenue, 'green'], ['ต้นทุนขาย', reportData.cost_of_sales, 'orange'],
      ['ค่าใช้จ่ายดำเนินงาน', reportData.operating_expenses, 'red'],
      ['กำไร (ขาดทุน) สุทธิ', reportData.net_profit, Number(reportData.net_profit) >= 0 ? 'green' : 'red']
    ];
    if (report === 'financial-position') return [
      ['สินทรัพย์รวม', positionTotals.asset, 'blue'], ['หนี้สินรวม', positionTotals.liability, 'red'],
      ['ส่วนของเจ้าของรวม', positionTotals.equity, 'purple']
    ];
    if (report === 'general-ledger') return [
      ['ยอดยกมา', reportData.opening_balance, 'blue'], ['เดบิตประจำงวด', reportData.total_debit, 'green'],
      ['เครดิตประจำงวด', reportData.total_credit, 'red'], ['ยอดคงเหลือ', reportData.closing_balance, 'purple']
    ];
    return [
      ['เดบิตประจำงวด', trialTotals.debit, 'green'], ['เครดิตประจำงวด', trialTotals.credit, 'red'],
      ['ผลต่างงบทดลอง', trialDifference, trialDifference === 0 ? 'blue' : 'red']
    ];
  };

  const exportRows = () => {
    if (report === 'trial-balance') return rows.map(row => [row.name, row.code, row.opening_debit, row.opening_credit, row.total_debit, row.total_credit, row.closing_debit, row.closing_credit]);
    if (report === 'general-ledger') return rows.map(row => [dayjs(row.entry_date).format('DD/MM/YYYY'), row.jv_number, row.description || row.entry_description, row.debit, row.credit, row.running_balance]);
    if (report === 'income-statement') return rows.map(row => [row.name, row.code, TYPE_LABELS[row.type], row.type === 'expense' ? -Number(row.period_amount) : Number(row.period_amount), row.type === 'expense' ? -Number(row.cumulative_amount) : Number(row.cumulative_amount)]);
    return rows.map(row => [row.code, row.name, TYPE_LABELS[row.type], row.amount]);
  };

  const exportHeaders = () => report === 'trial-balance'
    ? ['ชื่อบัญชี', 'รหัสบัญชี', 'ยอดยกมา เดบิต', 'ยอดยกมา เครดิต', 'งวด เดบิต', 'งวด เครดิต', 'คงเหลือ เดบิต', 'คงเหลือ เครดิต']
    : report === 'general-ledger' ? ['วันที่', 'เลขที่เอกสาร', 'คำอธิบาย', 'เดบิต', 'เครดิต', 'ยอดคงเหลือ']
      : report === 'income-statement' ? ['ชื่อบัญชี', 'รหัสบัญชี', 'ประเภทบัญชี', 'ประจำงวด', 'จนถึงงวดนี้']
        : ['รหัสบัญชี', 'ชื่อบัญชี', 'ประเภทบัญชี', 'ยอดคงเหลือ'];

  const downloadExcel = () => {
    if (!rows.length) return message.warning('ไม่มีข้อมูลสำหรับส่งออก');
    const title = REPORTS.find(item => item.key === report)?.label;
    const escape = value => String(value ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const html = `<html><head><meta charset="UTF-8"></head><body><h2>${title}</h2><table border="1"><thead><tr>${exportHeaders().map(item => `<th>${item}</th>`).join('')}</tr></thead><tbody>${exportRows().map(row => `<tr>${row.map(value => `<td>${escape(value)}</td>`).join('')}</tr>`).join('')}</tbody></table></body></html>`;
    const url = URL.createObjectURL(new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${report}-${dayjs().format('YYYYMMDD')}.xls`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const createGenericPdf = () => {
    if (!rows.length) return message.warning('ไม่มีข้อมูลสำหรับส่งออก');
    if (!window.pdfMake) return message.error('ไม่พบระบบสร้าง PDF');
    const title = REPORTS.find(item => item.key === report)?.label;
    const subtitle = report === 'financial-position'
      ? `ณ วันที่ ${asOfDate.format('DD/MM/YYYY')}`
      : `ระหว่างวันที่ ${period[0].format('DD/MM/YYYY')} ถึง ${period[1].format('DD/MM/YYYY')}`;
    return window.pdfMake.createPdf({
      pageOrientation: report === 'trial-balance' ? 'landscape' : 'portrait',
      defaultStyle: { font: 'THSarabunNew', fontSize: 13 },
      content: [
        { text: title, style: 'header', alignment: 'center' },
        { text: subtitle, alignment: 'center', margin: [0, 0, 0, 12] },
        { table: { headerRows: 1, widths: exportHeaders().map(() => '*'), body: [exportHeaders(), ...exportRows().map(row => row.map(value => typeof value === 'number' ? money(value) : value))] } }
      ],
      styles: { header: { fontSize: 22, bold: true } }
    });
  };

  const createPdfDataUrl = async () => {
    if (!rows.length) throw new Error('ไม่มีข้อมูลสำหรับส่งออก');
    if (report === 'trial-balance' || report === 'income-statement' || report === 'financial-position') {
      const companyResponse = await api.get('/api/settings/company');
      const company = companyResponse.data?.data || companyResponse.data || {};
      if (report === 'trial-balance') {
        return generateTrialBalancePdf({ rows, company, period });
      }
      if (report === 'income-statement') {
        return generateIncomeStatementPdf({ rows, summary: reportData, company, period });
      }
      return generateFinancialPositionPdf({ rows, company, asOfDate });
    }
    const generator = createGenericPdf();
    if (!generator) throw new Error('ไม่สามารถสร้าง PDF ได้');
    return new Promise(resolve => generator.getDataUrl(resolve));
  };

  const previewPdf = async () => {
    if (!rows.length) return message.warning('ไม่มีข้อมูลสำหรับส่งออก');
    setPdfPreviewOpen(true);
    setPdfPreviewLoading(true);
    setPdfDataUrl('');
    try {
      setPdfDataUrl(await createPdfDataUrl());
    } catch (error) {
      setPdfPreviewOpen(false);
      message.error(error.message || 'ไม่สามารถสร้างตัวอย่าง PDF ได้');
    } finally {
      setPdfPreviewLoading(false);
    }
  };

  const downloadPdf = async () => {
    if (!rows.length) return message.warning('ไม่มีข้อมูลสำหรับส่งออก');
    try {
      const dataUrl = pdfDataUrl || await createPdfDataUrl();
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `${report}-${dayjs().format('YYYYMMDD')}.pdf`;
      link.click();
    } catch (error) {
      message.error(error.message || 'ดาวน์โหลด PDF ไม่สำเร็จ');
    }
  };

  const tableSummary = () => {
    if (!rows.length || report !== 'trial-balance') return null;
    return (
      <Table.Summary.Row className="report-total-row">
        <Table.Summary.Cell index={0}>ยอดรวม</Table.Summary.Cell>
        <Table.Summary.Cell index={1} />
        {[trialTotals.openingDebit, trialTotals.openingCredit, trialTotals.debit, trialTotals.credit, trialTotals.closingDebit, trialTotals.closingCredit].map((value, index) => (
          <Table.Summary.Cell index={index + 2} align="right" key={index}>{money(value)}</Table.Summary.Cell>
        ))}
      </Table.Summary.Row>
    );
  };

  const mobileRows = rows.slice((mobilePage - 1) * mobilePageSize, mobilePage * mobilePageSize);

  const amountBlock = (label, value, tone = '') => (
    <div className={`mobile-report-amount ${tone}`}>
      <span>{label}</span>
      <strong>{money(value)} <small>บาท</small></strong>
    </div>
  );

  const debitCreditBlock = (label, debit, credit, emphasized = false) => (
    <div className={`mobile-report-period ${emphasized ? 'emphasized' : ''}`}>
      <div className="mobile-report-period-title">{label}</div>
      <div className="mobile-report-pair">
        {amountBlock('เดบิต', debit, 'debit')}
        {amountBlock('เครดิต', credit, 'credit')}
      </div>
    </div>
  );

  const renderMobileRow = (row, index) => {
    const rowKey = `${row.id || row.entry_id || row.code || 'row'}-${index}`;

    if (report === 'trial-balance') {
      return (
        <article className="mobile-report-card trial-balance-card" key={rowKey}>
          <header className="mobile-report-card-header">
            <div className="mobile-report-account">
              <strong>{row.name || '-'}</strong>
              <span>{row.code || '-'}</span>
            </div>
          </header>
          {debitCreditBlock('ยอดยกมา', row.opening_debit, row.opening_credit)}
          {debitCreditBlock('รายการประจำงวด', row.total_debit, row.total_credit)}
          {debitCreditBlock('ยอดคงเหลือ', row.closing_debit, row.closing_credit, true)}
        </article>
      );
    }

    if (report === 'general-ledger') {
      return (
        <article className="mobile-report-card ledger-card" key={rowKey}>
          <header className="mobile-report-card-header ledger-header">
            <div>
              <strong>{dayjs(row.entry_date).format('DD/MM/YYYY')}</strong>
              <span>{row.jv_number || '-'}</span>
            </div>
            <span className="mobile-running-balance">คงเหลือ {money(row.running_balance)} บาท</span>
          </header>
          <p className="mobile-report-description">{row.description || row.entry_description || '-'}</p>
          <div className="mobile-report-pair ledger-values">
            {amountBlock('เดบิต', row.debit, 'debit')}
            {amountBlock('เครดิต', row.credit, 'credit')}
          </div>
        </article>
      );
    }

    if (report === 'income-statement') {
      const multiplier = row.type === 'expense' ? -1 : 1;
      return (
        <article className={`mobile-report-card statement-card ${row.type || ''}`} key={rowKey}>
          <header className="mobile-report-card-header">
            <div className="mobile-report-account">
              <strong>{row.name || '-'}</strong>
              <span>{row.code || '-'}</span>
            </div>
            <span className={`account-tag ${row.type}`}>{TYPE_LABELS[row.type] || row.type}</span>
          </header>
          <div className="mobile-report-pair statement-values">
            {amountBlock('ประจำงวด', multiplier * Number(row.amount || row.period_amount || 0), row.type === 'expense' ? 'credit' : 'income')}
            {amountBlock('จนถึงงวดนี้', multiplier * Number(row.cumulative_amount || 0), row.type === 'expense' ? 'credit' : 'income')}
          </div>
        </article>
      );
    }

    return (
      <article className={`mobile-report-card position-card ${row.type || ''}`} key={rowKey}>
        <header className="mobile-report-card-header">
          <div className="mobile-report-account">
            <strong>{row.name || '-'}</strong>
            <span>{row.code || '-'}</span>
          </div>
          <span className={`account-tag ${row.type}`}>{TYPE_LABELS[row.type] || row.type}</span>
        </header>
        <div className="mobile-position-balance">
          <span>ยอดคงเหลือ</span>
          <strong>{money(row.amount)} <small>บาท</small></strong>
        </div>
      </article>
    );
  };

  const mobileReportContent = (
    <div className="mobile-report-list">
      {report === 'trial-balance' && rows.length > 0 && (
        <div className="mobile-trial-totals">
          <div className="mobile-trial-totals-title">ยอดรวมงบทดลอง</div>
          {debitCreditBlock('ยอดยกมา', trialTotals.openingDebit, trialTotals.openingCredit)}
          {debitCreditBlock('รายการประจำงวด', trialTotals.debit, trialTotals.credit)}
          {debitCreditBlock('ยอดคงเหลือ', trialTotals.closingDebit, trialTotals.closingCredit, true)}
        </div>
      )}
      <Spin spinning={loading}>
        {!loading && !rows.length ? (
          <Empty description="ไม่มีข้อมูลในช่วงเวลาที่เลือก" />
        ) : (
          <div className="mobile-report-cards">{mobileRows.map(renderMobileRow)}</div>
        )}
      </Spin>
      {rows.length > mobilePageSize && (
        <Pagination
          current={mobilePage}
          total={rows.length}
          pageSize={mobilePageSize}
          showSizeChanger={false}
          simple
          size="small"
          onChange={page => {
            setMobilePage(page);
            document.querySelector('.mobile-report-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        />
      )}
    </div>
  );

  return (
    <div className="accounting-reports-page">
      <div className="reports-heading">
        <Title level={2}><PieChartOutlined /> งบการเงิน</Title>
        <Button type="primary" icon={<SettingOutlined />} onClick={() => navigate('/accounting/settings')}>{isMobile ? 'ตั้งค่า' : 'ตั้งค่าผังบัญชี'}</Button>
      </div>

      <Card bordered={false} className="reports-card">
        <Tabs items={REPORTS} activeKey={report} onChange={changeReport} />

        <div className="report-toolbar">
          {report === 'general-ledger' && (
            <Select className="account-select" showSearch optionFilterProp="label" placeholder="เลือกบัญชี"
              value={accountId} onChange={value => { setAccountId(value); loadReport({ accountId: value }); }}
              options={accounts.map(account => ({ value: account.id, label: `${account.code} - ${account.name}` }))} />
          )}
          <div className="date-filter-area">
            {report !== 'financial-position' && <div className="quick-filters">
              {QUICK_FILTERS.map(([key, label]) => <Button size="small" type={activeQuick === key ? 'primary' : 'default'} key={key} onClick={() => applyQuickFilter(key)}>{label}</Button>)}
            </div>}
            {report === 'financial-position' ? (
              <Space><Text>ณ วันที่:</Text><DatePicker value={asOfDate} onChange={value => { if (value) { setAsOfDate(value); setActiveQuick(''); loadReport({asOfDate:value}); } }} format="DD/MM/YYYY" /></Space>
            ) : (
              <RangePicker value={period} onChange={value => { if (value) { setPeriod(value); setActiveQuick(''); loadReport({period:value}); } }} format="DD/MM/YYYY" allowClear={false} />
            )}
          </div>
          <Space wrap className="report-actions">
            <Button icon={<ReloadOutlined />} onClick={() => loadReport()}>ประมวลผล</Button>
            <Button icon={<FilePdfOutlined />} onClick={previewPdf} disabled={loading || !rows.length}>พิมพ์ PDF</Button>
            <Button icon={<DownloadOutlined />} onClick={downloadPdf} disabled={loading || !rows.length}>ดาวน์โหลด</Button>
            <Button icon={<FileExcelOutlined />} onClick={downloadExcel} disabled={loading || !rows.length}>ส่งออก Excel</Button>
          </Space>
        </div>

        <Row gutter={[16, 16]} className="summary-grid">
          {summaryCards().map(([label, value, color]) => (
            <Col xs={24} sm={12} lg={24 / summaryCards().length} key={label}>
              <Card size="small" className={`summary-card ${color}`}>
                <Statistic title={label} value={Number(value || 0)} precision={2} suffix="฿" />
              </Card>
            </Col>
          ))}
        </Row>

        {report === 'income-statement' && <>
          <FinancialSummary data={{documents:reportData.documents,ledger:reportData,metadata:reportData.metadata,reconciliation:reportData.reconciliation}} />
          <Card title="รายละเอียดกระทบยอด" style={{marginBottom:16}}>
            <Text type="secondary">ยอดเอกสารใช้วันที่เอกสาร · ยอดบัญชีใช้วันที่ลงบัญชี · การซื้อสินทรัพย์และรายการปรับปรุงอาจทำให้ยอดต่างกัน</Text>
            <Table size="small" rowKey={row=>`${row.source_type}-${row.source_id}`} scroll={{x:850}} dataSource={reportData.reconciliation?.rows||[]} columns={[
              {title:'เอกสาร',dataIndex:'document_number',render:(v,row)=><Button type="link" onClick={()=>navigate(`${row.document_url}?view_id=${row.source_id}`)}>{v}</Button>},
              {title:'วันที่เอกสาร',dataIndex:'document_date',render:v=>dayjs(v).format('DD/MM/YYYY')},
              {title:'ก่อน VAT',dataIndex:'before_vat',render:money},
              {title:'เหตุผล',dataIndex:'reasons',render:values=>values.map(v=>({pending_approval:'รออนุมัติ',unposted:'รอลงบัญชี',different_entry_date:'วันที่ลงบัญชีต่างจากเอกสาร',asset_or_non_expense:'สินทรัพย์/รายการนอกค่าใช้จ่าย',gl_amount_difference:'ยอด GL ต่างจากเอกสาร',document_amount_anomaly:'ยอดเอกสารต้องตรวจสอบ'}[v]||v)).join(', ')},
              {title:'สมุดรายวัน',dataIndex:'jv_number',render:(v,row)=>row.entry_id?<Button type="link" onClick={()=>navigate(row.journal_url)}>{v}</Button>:'-'}
            ]}/>
            <Table size="small" rowKey="entry_id" scroll={{x:650}} dataSource={reportData.reconciliation?.adjustments||[]} columns={[
              {title:'รายการปรับปรุง/รายการต่างงวด',dataIndex:'jv_number',render:(v,row)=><Button type="link" onClick={()=>navigate(`/accounting/journal?entry_id=${row.entry_id}`)}>{v}</Button>},
              {title:'คำอธิบาย',dataIndex:'description'},{title:'รายได้ตาม GL',dataIndex:'income',render:money},{title:'ค่าใช้จ่ายตาม GL',dataIndex:'expense',render:money}
            ]}/>
          </Card>
        </>}

        {report === 'general-ledger' && reportData.account && (
          <div className="selected-account"><BarChartOutlined /> {reportData.account.code} - {reportData.account.name}</div>
        )}

        {isMobile ? mobileReportContent : (
          <Table rowKey={(row, index) => `${row.id || row.entry_id || 'row'}-${index}`} columns={columns} dataSource={rows}
            size="middle" loading={loading} scroll={{ x: report === 'trial-balance' ? 1200 : 850 }} pagination={{ defaultPageSize: 20, showSizeChanger: true }}
            locale={{ emptyText: <Empty description="ไม่มีข้อมูลในช่วงเวลาที่เลือก" /> }} summary={tableSummary} />
        )}
      </Card>

      <Modal
        title={`ตัวอย่างเอกสาร ${REPORTS.find(item => item.key === report)?.label || ''}`}
        open={pdfPreviewOpen}
        onCancel={() => setPdfPreviewOpen(false)}
        width={900}
        centered
        destroyOnClose
        footer={[
          <Button key="close" onClick={() => setPdfPreviewOpen(false)}>ปิด</Button>,
          <Button key="download" type="primary" icon={<DownloadOutlined />} disabled={!pdfDataUrl} onClick={downloadPdf}>ดาวน์โหลด PDF</Button>
        ]}
        className="financial-pdf-modal"
      >
        <Spin spinning={pdfPreviewLoading} tip="กำลังสร้างเอกสาร...">
          {pdfDataUrl ? <iframe src={pdfDataUrl} title="ตัวอย่างรายงาน PDF" /> : <div className="pdf-preview-placeholder" />}
        </Spin>
      </Modal>
    </div>
  );
};

export default AccountingReportsPage;
